import { useMemo } from 'react'
import { z } from 'zod'

const decodeEvaluationSlugSchema = z.object({
  id: z.string(),
  evaluationVersion: z.coerce.number().default(-1),
  slug: z.string(),
})

export type DecodedEvaluation = z.infer<typeof decodeEvaluationSlugSchema>

export const decodeEvaluationSlug = (evaluationSlug: string = '') => {
  const [id, evaluationVersion] = decodeURIComponent(evaluationSlug).split(':')
  const hasParts =
    typeof id === 'string' && typeof evaluationVersion === 'string'
  if (!hasParts) {
    return null
  }
  return decodeEvaluationSlugSchema.parse({
    id,
    evaluationVersion,
    slug: evaluationSlug,
  })
}

export const decodeSafeEvaluationSlugAsync = (evaluationSlug: string) => {
  const [id, evaluationVersion] = decodeURIComponent(evaluationSlug).split(':')
  return decodeEvaluationSlugSchema.parseAsync({
    id,
    evaluationVersion,
    slug: decodeURIComponent(evaluationSlug),
  })
}

export const decodeSafeEvaluationSlug = (evaluationSlug: string) => {
  const [id, evaluationVersion] = decodeURIComponent(evaluationSlug).split(':')
  return decodeEvaluationSlugSchema.parse({
    id,
    evaluationVersion,
    slug: decodeURIComponent(evaluationSlug),
  })
}

export const useDecodedEvaluationSlug = (evaluationSlug?: string) =>
  useMemo(() => decodeEvaluationSlug(evaluationSlug), [evaluationSlug])

export const createUUID = () => {
  let dt = new Date().getTime()
  const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (dt + Math.random() * 16) % 16 | 0
    dt = Math.floor(dt / 16)
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
  return uuid
}

export function generateExtendedBindId(baseBindId: string, extension: string) {
  const bindIdChunks = baseBindId.split(':')

  const idChunk = bindIdChunks.pop()
  if (!idChunk) {
    return baseBindId
  }
  bindIdChunks.push(extension)
  bindIdChunks.push(idChunk)
  return bindIdChunks.join(':')
}
